import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

/**
 * ImageCarousel - Carrossel de imagens estilo Instagram
 * Setas circulares semi-transparentes sobrepostas à mídia (não nas laterais)
 */
const ImageCarousel = ({ images = [], media = [], alt = 'Imagem', maxWidth = '100%' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Unificar fontes de dados: Priorizar media (estruturado), fallback para images (strings)
  const items = media.length > 0 ? media : images.map(url => ({ type: 'image', url }));

  useEffect(() => {
    setCurrentIndex(0);
  }, [items.length, items[0]?.url]);

  if (!items || items.length === 0) return null;

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => Math.min(items.length - 1, prev + 1));
  };

  const showArrows = items.length > 1;
  const safeIndex = Math.max(0, Math.min(currentIndex, items.length - 1));
  const currentItem = items[safeIndex];

  // Helper para renderizar o item atual
  const renderItem = (item) => {
    if (!item) return null;
    if (item.type === 'video') {
      return (
        <video
          src={item.url}
          poster={item.poster}
          controls
          playsInline
          onError={(e) => {
            console.error('[ImageCarousel] Video Error:', e.nativeEvent);
          }}
          style={{
            width: '100%',           // Preenche o container inline-block
            maxWidth: '100%',
            maxHeight: '600px',
            height: 'auto',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'block'
          }}
        />
      );
    }

    // Default: Imagem
    return (
      <img
        src={item.url || item} // Suporta objeto ou string direta
        alt={`${alt} ${currentIndex + 1}`}
        style={{
          maxWidth: '100%',
          maxHeight: '600px',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          display: 'block'
        }}
        loading="lazy"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  };

  return (
    // Container externo: apenas centraliza e limita largura máxima — sem bgcolor nem overflow
    <Box
      sx={{
        width: '100%',
        maxWidth: maxWidth,
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* Container interno: abraça exatamente a mídia e serve de âncora para as setas sobrepostas.
           Para imagens: inline-block abraça o tamanho natural da imagem.
           Para vídeos:  width:100% garante que o vídeo preenche o espaço disponível. */}
      <Box
        sx={{
          position: 'relative',
          display: 'inline-block',
          width: currentItem?.type === 'video' ? '100%' : 'auto',  // Vídeo precisa de largura total
          lineHeight: 0,        // elimina espaço extra abaixo de <img>
          borderRadius: '8px',
          overflow: 'hidden',   // garante que as setas respeitam as bordas arredondadas
          minHeight: currentItem?.type === 'video' ? 0 : '300px',  // Vídeo define sua própria altura
        }}
      >
        {renderItem(currentItem)}

        {/* Setas condicionais — sobrepostas diretamente à mídia */}
        {showArrows && (
          <>
            {currentIndex > 0 && (
              <IconButton
                onClick={handlePrev}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#262626',
                  width: 30,
                  height: 30,
                  zIndex: 2,
                  boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                  '& .MuiSvgIcon-root': { fontSize: 16 }
                }}
              >
                <ChevronLeft />
              </IconButton>
            )}

            {currentIndex < items.length - 1 && (
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#262626',
                  width: 30,
                  height: 30,
                  zIndex: 2,
                  boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                  '& .MuiSvgIcon-root': { fontSize: 16 }
                }}
              >
                <ChevronRight />
              </IconButton>
            )}
          </>
        )}

        {/* Indicadores de posição (bolinhas) — sobrepostos na parte inferior da mídia */}
        {showArrows && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '4px',
              zIndex: 2,
              pointerEvents: 'none', // Para não atrapalhar controles do vídeo
            }}
          >
            {items.map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: idx === currentIndex ? '#0095f6' : 'rgba(255,255,255,0.5)',
                  transition: 'background-color 0.2s'
                }}
              />
            ))}
          </Box>
        )}

        {/* Contador (1/5) — sobreposto no canto superior direito da mídia */}
        {showArrows && (
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#fff',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500
            }}
          >
            {currentIndex + 1}/{items.length}
          </Box>
        )}
      </Box>
    </Box>
  );
};

ImageCarousel.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string),
  media: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string,
    url: PropTypes.string,
    poster: PropTypes.string
  })),
  alt: PropTypes.string,
  maxWidth: PropTypes.string
};

export default ImageCarousel;
